import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { tracking } from 'noddi-web';
import { useNavigate } from 'react-router-dom';
import { useStationaryBookingActions } from '../../../../stores/StationaryBookingStore';
import type { StationaryGarageOption } from '../../../../types/stationaryBooking';
import type { BookingChoicePhoto } from './bookingChoicePhotos';

interface Props {
  photo?: BookingChoicePhoto;
  garageOptions: StationaryGarageOption[];
}

/** Secondary path: light card where each location row jumps into that garage's booking flow. */
export function ServiceCenterCard({ photo, garageOptions }: Props) {
  const navigate = useNavigate();
  const { setServiceCenter } = useStationaryBookingActions();
  const facts = [t`Modern workshop`, t`Quick service - often same day`];

  function setGarageAndNavigate(garage: StationaryGarageOption) {
    if (!garage.hasFutureCapacity) {
      return;
    }

    tracking.track('serviceCenterSelected', {
      serviceCenterId: garage.serviceDepartmentId,
      serviceCenterName: garage.name,
      garageId: garage.garageId
    });
    setServiceCenter({
      serviceDepartmentId: garage.serviceDepartmentId,
      name: garage.name,
      serviceOrganizationId: garage.serviceOrganizationId,
      garageId: garage.garageId
    });
    navigate(customerRoutes.newStationaryBookingCar.getPath());
  }

  return (
    <NoddiBasicCard className='flex h-full flex-col overflow-hidden rounded-[22px] border-none bg-card-neutral-background p-0 shadow-none md:rounded-3xl'>
      {photo && (
        <div className='relative h-[150px] shrink-0 md:h-60'>
          <picture>
            <source srcSet={photo.avif} type='image/avif' />
            <source srcSet={photo.webp} type='image/webp' />
            <img
              src={photo.jpg}
              alt={t`Inside our service center`}
              className='absolute inset-0 size-full object-cover'
            />
          </picture>
        </div>
      )}
      <div className='flex flex-1 flex-col gap-4 p-5 pb-6 md:gap-5 md:p-6'>
        <div className='flex flex-col gap-1.5'>
          <p className='text-xs font-semibold tracking-[0.14em] text-neutral-500 uppercase'>
            {t`Come to one of our locations`}
          </p>
          <h2 className='text-xl leading-snug font-bold tracking-tight text-neutral-900 md:text-2xl'>
            {t`Visit our service center`}
          </h2>
        </div>
        <div className='flex flex-col gap-2'>
          {facts.map((fact) => (
            <div key={fact} className='flex items-center gap-3 text-sm text-neutral-600'>
              <span className='size-[5px] shrink-0 rounded-full bg-secondary' />
              {fact}
            </div>
          ))}
        </div>
        <div className='mt-auto flex flex-col gap-2.5'>
          {garageOptions.map((location) => {
            const unavailableId = `garage-${location.serviceDepartmentId}-unavailable`;
            return (
              <div key={location.serviceDepartmentId} className='flex flex-col gap-1'>
                <button
                  type='button'
                  aria-disabled={!location.hasFutureCapacity || undefined}
                  aria-describedby={!location.hasFutureCapacity ? unavailableId : undefined}
                  // aria-disabled keeps the reason focusable, so the click itself must refuse.
                  onClick={() => location.hasFutureCapacity && setGarageAndNavigate(location)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-xl border border-[#dddbd6] bg-white p-4 text-left transition-colors focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden md:px-5 md:hover:border-[#c9c6c0] md:hover:bg-[#faf9f7]',
                    !location.hasFutureCapacity && 'opacity-50 md:hover:border-[#dddbd6] md:hover:bg-white'
                  )}
                >
                  <span className='flex-1 text-sm font-semibold text-neutral-900 md:text-base'>{location.name}</span>
                  <NoddiIcon name='ChevronRight' size='small' strokeWidth={2} className='shrink-0 text-neutral-900' />
                </button>
                {!location.hasFutureCapacity && (
                  <p id={unavailableId} className='px-1 text-xs text-neutral-500'>
                    <Trans>No available appointments in the near future</Trans>
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </NoddiBasicCard>
  );
}
